import React, { useEffect, useRef, useState } from 'react';
import { sound } from './SoundSystem';

export default function ThreeBackground({ mousePosition }) {
  const mountRef = useRef(null);
  const [geoMode, setGeoMode] = useState(0); // 0: Icosahedron, 1: TorusKnot, 2: Dodecahedron
  const [wireframeOnly, setWireframeOnly] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [showControls, setShowControls] = useState(false);

  const sceneState = useRef({
    scene: null,
    camera: null,
    renderer: null,
    coreMesh: null,
    wireMesh: null,
    particleSystem: null,
    animId: null,
    mouseTarget: { x: 0, y: 0 },
    currentRot: { x: 0, y: 0 },
    geoList: ['Icosahedron', 'Torus Knot', 'Cyber Crystal']
  });

  // Update mouse target
  useEffect(() => {
    if (!mousePosition) return;
    const normX = (mousePosition.x / window.innerWidth) * 2 - 1;
    const normY = -(mousePosition.y / window.innerHeight) * 2 + 1;
    sceneState.current.mouseTarget = { x: normX * 0.6, y: normY * 0.6 };
  }, [mousePosition]);

  // Init Three.js Scene
  useEffect(() => {
    if (!window.THREE || !mountRef.current) return;
    const THREE = window.THREE;
    const container = mountRef.current;
    const width = container.clientWidth;
    const height = container.clientHeight;

    // Scene & Camera
    const scene = new THREE.Scene();
    sceneState.current.scene = scene;

    const camera = new THREE.PerspectiveCamera(45, width / height, 0.1, 1000);
    camera.position.z = 24;
    sceneState.current.camera = camera;

    // WebGL Renderer
    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, powerPreference: 'high-performance' });
    renderer.setSize(width, height);
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    container.innerHTML = '';
    container.appendChild(renderer.domElement);
    sceneState.current.renderer = renderer;

    // Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.4);
    scene.add(ambientLight);

    const pointLight1 = new THREE.PointLight(0x00f59b, 2.5, 50);
    pointLight1.position.set(12, 12, 10);
    scene.add(pointLight1);

    const pointLight2 = new THREE.PointLight(0x00e5ff, 2.0, 50);
    pointLight2.position.set(-12, -10, 8);
    scene.add(pointLight2);

    const pointLight3 = new THREE.PointLight(0x8b5cf6, 1.5, 40);
    pointLight3.position.set(0, 15, -5);
    scene.add(pointLight3);

    // Build 3D Core Group
    const coreGroup = new THREE.Group();
    scene.add(coreGroup);

    // Initial Geometries
    const createGeometries = (typeIndex) => {
      // Clear previous
      while (coreGroup.children.length > 0) {
        const obj = coreGroup.children[0];
        if (obj.geometry) obj.geometry.dispose();
        if (obj.material) obj.material.dispose();
        coreGroup.remove(obj);
      }

      let solidGeo, wireGeo;
      if (typeIndex === 0) {
        solidGeo = new THREE.IcosahedronGeometry(4.2, 1);
        wireGeo = new THREE.IcosahedronGeometry(4.8, 1);
      } else if (typeIndex === 1) {
        solidGeo = new THREE.TorusKnotGeometry(3.2, 0.85, 100, 16);
        wireGeo = new THREE.TorusKnotGeometry(3.6, 0.95, 60, 10);
      } else {
        solidGeo = new THREE.DodecahedronGeometry(4.0, 0);
        wireGeo = new THREE.DodecahedronGeometry(4.6, 0);
      }

      // Solid Core Material
      const solidMat = new THREE.MeshPhongMaterial({
        color: 0x071126,
        emissive: 0x031828,
        specular: 0x00f59b,
        shininess: 90,
        flatShading: true,
        transparent: true,
        opacity: wireframeOnly ? 0.15 : 0.85
      });
      const coreMesh = new THREE.Mesh(solidGeo, solidMat);
      coreGroup.add(coreMesh);
      sceneState.current.coreMesh = coreMesh;

      // Wireframe Halo Material
      const wireMat = new THREE.MeshBasicMaterial({
        color: 0x00e5ff,
        wireframe: true,
        transparent: true,
        opacity: 0.75
      });
      const wireMesh = new THREE.Mesh(wireGeo, wireMat);
      coreGroup.add(wireMesh);
      sceneState.current.wireMesh = wireMesh;

      // Outer Pulsing Ring
      const ringGeo = new THREE.RingGeometry(6.2, 6.35, 64);
      const ringMat = new THREE.MeshBasicMaterial({
        color: 0x00f59b,
        side: THREE.DoubleSide,
        transparent: true,
        opacity: 0.35
      });
      const ringMesh = new THREE.Mesh(ringGeo, ringMat);
      ringMesh.rotation.x = Math.PI / 2;
      coreGroup.add(ringMesh);
    };

    createGeometries(geoMode);
    sceneState.current.createGeometries = createGeometries;

    // 3D Particles Field (Star Galaxy)
    const particleCount = 650;
    const particleGeo = new THREE.BufferGeometry();
    const positions = new Float32Array(particleCount * 3);
    const colors = new Float32Array(particleCount * 3);

    const palette = [
      new THREE.Color(0x00f59b),
      new THREE.Color(0x00e5ff),
      new THREE.Color(0x8b5cf6),
      new THREE.Color(0xffffff)
    ];

    for (let i = 0; i < particleCount; i++) {
      const i3 = i * 3;
      positions[i3] = (Math.random() - 0.5) * 60;
      positions[i3 + 1] = (Math.random() - 0.5) * 45;
      positions[i3 + 2] = (Math.random() - 0.5) * 40 - 5;

      const col = palette[Math.floor(Math.random() * palette.length)];
      colors[i3] = col.r;
      colors[i3 + 1] = col.g;
      colors[i3 + 2] = col.b;
    }

    particleGeo.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    particleGeo.setAttribute('color', new THREE.BufferAttribute(colors, 3));

    const particleMat = new THREE.PointsMaterial({
      size: 0.22,
      vertexColors: true,
      transparent: true,
      opacity: 0.8
    });

    const particles = new THREE.Points(particleGeo, particleMat);
    scene.add(particles);
    sceneState.current.particleSystem = particles;

    // Render Animation Loop
    let clock = new THREE.Clock();

    const animate = () => {
      sceneState.current.animId = requestAnimationFrame(animate);

      const elapsedTime = clock.getElapsedTime();

      if (!isPaused) {
        // Rotate Core Group
        coreGroup.rotation.y += 0.006;
        coreGroup.rotation.x += 0.003;

        // Breathe pulse
        const scalePulse = 1 + Math.sin(elapsedTime * 1.5) * 0.04;
        coreGroup.scale.set(scalePulse, scalePulse, scalePulse);

        // Slow particle drift
        particles.rotation.y = elapsedTime * 0.02;
        particles.rotation.x = Math.sin(elapsedTime * 0.03) * 0.05;
      }

      // Smooth Mouse Parallax
      const mouse = sceneState.current.mouseTarget;
      camera.position.x += (mouse.x * 4 - camera.position.x) * 0.04;
      camera.position.y += (mouse.y * 3 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
    };

    animate();

    // Handle Window Resize
    const handleResize = () => {
      if (!mountRef.current || !renderer || !camera) return;
      const w = mountRef.current.clientWidth;
      const h = mountRef.current.clientHeight;
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
      renderer.setSize(w, h);
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      if (sceneState.current.animId) {
        cancelAnimationFrame(sceneState.current.animId);
      }
      if (renderer && renderer.domElement) {
        renderer.domElement.remove();
      }
    };
  }, []);

  // Update geometry when geoMode or wireframe changes
  useEffect(() => {
    if (sceneState.current.createGeometries) {
      sceneState.current.createGeometries(geoMode);
    }
  }, [geoMode, wireframeOnly]);

  const cycleGeometry = () => {
    sound.playClick();
    setGeoMode((prev) => (prev + 1) % 3);
  };

  const toggleWireframe = () => {
    sound.playClick();
    setWireframeOnly((prev) => !prev);
  };

  const togglePause = () => {
    sound.playClick();
    setIsPaused((prev) => !prev);
  };

  return (
    <div className="three-background-wrapper">
      <div ref={mountRef} className="three-canvas-container" />
      
      {/* 3D Visualizer HUD Controls Widget */}
      <div className={`three-hud-controls ${showControls ? 'expanded' : ''}`}>
        <button 
          className="three-hud-toggle"
          onClick={() => {
            sound.playClick();
            setShowControls(!showControls);
          }}
          title="Contrôles 3D Interactifs"
        >
          <i className="fa-solid fa-cube"></i>
          <span>Mode 3D</span>
        </button>

        {showControls && (
          <div className="three-hud-panel">
            <div className="hud-panel-header">
              <span className="hud-tag">WEBGL 3D ENGINE</span>
              <span className="hud-geo-name">{sceneState.current.geoList[geoMode]}</span>
            </div>
            <div className="hud-buttons">
              <button className="hud-btn" onClick={cycleGeometry} title="Changer la forme 3D">
                <i className="fa-solid fa-rotate"></i>
                Forme
              </button>
              <button className={`hud-btn ${wireframeOnly ? 'active' : ''}`} onClick={toggleWireframe} title="Basculer le fil de fer">
                <i className="fa-solid fa-vector-square"></i>
                {wireframeOnly ? 'Plein' : 'Filaire'}
              </button>
              <button className={`hud-btn ${isPaused ? 'active' : ''}`} onClick={togglePause} title="Pause / Reprendre rotation">
                <i className={`fa-solid ${isPaused ? 'fa-play' : 'fa-pause'}`}></i>
                {isPaused ? 'Animer' : 'Fixer'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
